import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import {
  COMPOSITION_GET_DATA_V2,
  COMPOSITION_GET_DATA_V3,
  type CompositionV2DataQuery,
  type CompositionV2DataQueryVariables,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
  METRIC_MODE_IDS,
  POSTING_GET_DATA,
  POSTING_SOURCE_IDS,
  type PostingsDataQuery,
} from '@revelio/data-access';

import { useMonthApiFilters } from '../../../../entity-summary/use-month-api-filters';
import { useAvailableDateRangeWithTrialLag } from '../../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { hasPrimaryFilter } from '../../../filters/utils/common';
import { getCompositionV3Variables } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { getMonthRefsFromCompositionV3Filters } from '../../../filters/utils/composition-v3/month-refs-from-v3-filters';
import {
  getFormattedDateString,
  utcToLocal,
} from '../../../filters/utils/dateUtils';
import { getCompositionV2Filters } from '../../../filters/utils/getCompositionV2Filters';
import { getDim1 } from '../../../filters/utils/getDim1';
import { getPostingFilters } from '../../../filters/utils/getPostingFilters';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';
import { adaptCompositionV3ForMarketConditions } from './adapt-composition-v3-for-market-conditions';
import { combineTwoQueryStatuses } from './market-condition-metrics-utils';

type CompositionApiMode = 'legacy' | 'v3';

function useCompositionAndPostingsQueriesInner(
  compositionApi: CompositionApiMode,
  primaryDimension: PrimaryFilterId
) {
  const compositionEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'month',
  });
  const dateRangeApiFilters = useMonthApiFilters();
  const compositionVariables = getCompositionV2Filters({
    primaryDimension,
    effectiveFilters: compositionEffectiveFilters,
    isSnapshot: false,
  });

  const compositionVariablesV3Base = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters: compositionEffectiveFilters,
    isSnapshot: false,
  });

  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const availableStartDate = utcToLocal(availableDateRange?.month.min);
  const availableEndDate = utcToLocal(availableDateRange?.month.max);
  const endDate =
    compositionVariables.filters.end_date ??
    getFormattedDateString(availableEndDate, 'month');
  const startDate =
    compositionVariables.filters.start_date ??
    getFormattedDateString(availableStartDate, 'month');

  const compositionVariablesV3 = {
    ...compositionVariablesV3Base,
    filters: {
      ...compositionVariablesV3Base.filters,
      start_date: startDate,
      end_date: endDate,
    },
  };

  const compositionPaused =
    !compositionVariables.entities ||
    (Array.isArray(compositionVariables.entities) &&
      compositionVariables.entities.length === 0) ||
    !startDate ||
    !endDate;

  const compositionPausedV3 =
    !compositionVariablesV3.entities ||
    (Array.isArray(compositionVariablesV3.entities) &&
      compositionVariablesV3.entities.length === 0) ||
    !startDate ||
    !endDate;

  const isV3Api = compositionApi === 'v3';

  const compositionQuery = isV3Api
    ? COMPOSITION_GET_DATA_V3
    : COMPOSITION_GET_DATA_V2;
  const compositionVariablesActive = isV3Api
    ? compositionVariablesV3
    : compositionVariables;
  const compositionPausedActive = isV3Api
    ? compositionPausedV3
    : compositionPaused;

  const [{ data: compositionDataUnion, status: compositionStatus }] =
    useUrqlQueryWithStatus<
      CompositionV2DataQuery | CompositionV3DataQuery,
      CompositionV2DataQueryVariables | CompositionV3DataQueryVariables
    >({
      query: compositionQuery,
      variables: compositionVariablesActive as CompositionV2DataQueryVariables &
        CompositionV3DataQueryVariables,
      pause: compositionPausedActive,
    });

  const dim1 = getDim1(primaryDimension);
  const postingEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'week',
  });
  const postingFilters = getPostingFilters(
    primaryDimension,
    postingEffectiveFilters
  );
  const isPrimaryFilterSet = hasPrimaryFilter({
    primaryDimension,
    filters: compositionEffectiveFilters,
  });

  const postingsPaused = !isPrimaryFilterSet || !dateRangeApiFilters;

  const [{ data: postingsData, status: postingsStatus }] =
    useUrqlQueryWithStatus<PostingsDataQuery>({
      query: POSTING_GET_DATA,
      variables: {
        dim1,
        filters: {
          ...postingFilters,
          provider: [POSTING_SOURCE_IDS.unified],
          metric_mode: METRIC_MODE_IDS.expectedHires,
        },
        ...(localStorage.getItem('skip_share_of_postings_scaling') === 'true'
          ? { skip_share_of_postings_scaling: true }
          : {}),
      },
      pause: postingsPaused,
    });

  const status = combineTwoQueryStatuses(compositionStatus, postingsStatus);

  const compositionDataLegacy = isV3Api
    ? undefined
    : (compositionDataUnion as CompositionV2DataQuery | undefined)
        ?.composition_v2;

  const compositionDataAdapted = isV3Api
    ? adaptCompositionV3ForMarketConditions(
        (compositionDataUnion as CompositionV3DataQuery | undefined)
          ?.composition_v3,
        getMonthRefsFromCompositionV3Filters(compositionVariablesV3.filters)
      )
    : undefined;

  return {
    compositionData: isV3Api ? compositionDataAdapted : compositionDataLegacy,
    postingsData: postingsData?.posting,
    status,
  };
}

export function useCompositionAndPostingsQueriesLegacy(
  primaryDimension: PrimaryFilterId
) {
  return useCompositionAndPostingsQueriesInner('legacy', primaryDimension);
}

export function useCompositionAndPostingsQueriesV3(
  primaryDimension: PrimaryFilterId
) {
  return useCompositionAndPostingsQueriesInner('v3', primaryDimension);
}

export function useCompositionAndPostingsQueries(
  primaryDimension: PrimaryFilterId
) {
  const isV3 = useUnleashFlag(FeatureFlag.CompositionV3);
  return useCompositionAndPostingsQueriesInner(
    isV3 ? 'v3' : 'legacy',
    primaryDimension
  );
}
